import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const precalculusUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Exponential and Logarithmic Functions",
  examWeight: "27–40%",
  focus: "Modeling Change Using Multiplication (Growth/Decay)",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Change in Arithmetic and Geometric Sequences",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sequences as Functions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Sequences are functions where the domain is restricted to whole numbers (<InlineMath math="0, 1, 2, \dots" />) or natural numbers. The graph consists of discrete points, not a connected curve.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Arithmetic Sequences</h3>
            <KeyTerm 
              term="Arithmetic Sequence" 
              definition="A sequence where the difference between consecutive terms is constant (common difference d)."
            />
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-4">
              <p className="font-semibold text-blue-800">Explicit Formula:</p>
              <BlockMath math="a_n = a_0 + d \cdot n" />
              <p className="text-sm text-slate-600 mt-2">Rate of Change: <strong>Additive</strong>. Behaves like a <strong>linear function</strong> with discrete domain.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Geometric Sequences</h3>
            <KeyTerm 
              term="Geometric Sequence" 
              definition="A sequence where the ratio between consecutive terms is constant (common ratio r)."
            />
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 my-4">
              <p className="font-semibold text-green-800">Explicit Formula:</p>
              <BlockMath math="g_n = g_0 \cdot r^n" />
              <p className="text-sm text-slate-600 mt-2">Rate of Change: <strong>Multiplicative</strong>. Behaves like an <strong>exponential function</strong> with discrete domain.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Change in Linear and Exponential Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Linear Functions</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="font-semibold text-blue-800">Analytic Form: <InlineMath math="f(x) = mx + b" /></p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><InlineMath math="b" />: Initial value (y-intercept)</li>
                <li><InlineMath math="m" />: Slope (additive rate of change)</li>
              </ul>
              <p className="text-sm text-slate-600 mt-3">
                <strong>Covariation:</strong> Over equal-length input intervals, output changes by a <strong>constant difference</strong> (addition).
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Exponential Functions</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <p className="font-semibold text-green-800">Analytic Form: <InlineMath math="f(x) = a \cdot b^x" /></p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><InlineMath math="a" />: Initial value (y-intercept at <InlineMath math="x=0" />)</li>
                <li><InlineMath math="b" />: Growth/Decay factor (base)</li>
              </ul>
              <p className="text-sm text-slate-600 mt-3">
                <strong>Covariation:</strong> Over equal-length input intervals, output changes by a <strong>constant ratio</strong> (multiplication).
              </p>
            </div>
          </section>

          <Mnemonic 
            title="Key Distinction"
            content="Linear functions ADD equal amounts over equal intervals; Exponential functions MULTIPLY by equal factors over equal intervals."
          />
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Exponential Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">General Form</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="f(x) = ab^x" />
              <p className="text-sm text-slate-600 mt-2">where <InlineMath math="a \neq 0" />, <InlineMath math="b > 0" />, and <InlineMath math="b \neq 1" /></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Growth vs. Decay</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Exponential Growth</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="a > 0" /> and <InlineMath math="b > 1" /></p>
                <p className="text-sm text-slate-600">Graph increases away from the asymptote.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Exponential Decay</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="a > 0" /> and <InlineMath math="0 < b < 1" /></p>
                <p className="text-sm text-slate-600">Graph decreases toward the asymptote.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Characteristics</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><strong>Domain:</strong> <InlineMath math="(-\infty, \infty)" /> (All real numbers)</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><strong>Range:</strong> <InlineMath math="(0, \infty)" /> if <InlineMath math="a > 0" />; <InlineMath math="(-\infty, 0)" /> if <InlineMath math="a < 0" /></p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><strong>Asymptote:</strong> Horizontal asymptote at <InlineMath math="y = 0" /> (unless vertically shifted)</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><strong>Concavity:</strong> Always concave up if <InlineMath math="a > 0" />, always concave down if <InlineMath math="a < 0" />. No inflection points.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">End Behavior (Growth, <InlineMath math="b > 1, a > 0" />)</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-slate-700"><InlineMath math="\lim_{x \to \infty} f(x) = \infty" /></p>
              <p className="text-slate-700 mt-2"><InlineMath math="\lim_{x \to -\infty} f(x) = 0" /></p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Exponential Function Manipulation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Fundamental Exponent Properties</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-slate-700"><strong>Product Property:</strong> <InlineMath math="b^m \cdot b^n = b^{m+n}" /></p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-slate-700"><strong>Power Property:</strong> <InlineMath math="(b^m)^n = b^{mn}" /></p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-slate-700"><strong>Negative Exponent:</strong> <InlineMath math="b^{-n} = \frac{1}{b^n}" /></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting Transformations</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
              <h4 className="font-bold text-amber-800">Horizontal Translation ↔ Vertical Dilation</h4>
              <p className="text-sm text-slate-700 mt-2">A horizontal shift can be rewritten as a vertical stretch/compression:</p>
              <BlockMath math="f(x) = b^{x+k} = b^x \cdot b^k = (b^k) \cdot b^x" />
              <p className="text-sm text-slate-600 mt-2">Here, <InlineMath math="b^k" /> becomes the new initial value <InlineMath math="a" />.</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-bold text-purple-800">Horizontal Dilation ↔ Change of Base</h4>
              <p className="text-sm text-slate-700 mt-2">A horizontal stretch/compression can be rewritten as a change in base:</p>
              <BlockMath math="f(x) = b^{cx} = (b^c)^x" />
              <p className="text-sm text-slate-600 mt-2">Here, <InlineMath math="b^c" /> becomes the new base.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: Exponential Function Context and Data Modeling",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Modeling Formula</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="f(x) = a \cdot b^x" />
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><InlineMath math="a" /> (Initial Value): The value at time <InlineMath math="t=0" /></li>
                <li><InlineMath math="b" /> (Growth Factor): Derived from the rate <InlineMath math="r" /></li>
              </ul>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="font-semibold text-green-800">Growth:</p>
                <p className="text-sm text-slate-700"><InlineMath math="b = 1 + r" /></p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="font-semibold text-red-800">Decay:</p>
                <p className="text-sm text-slate-700"><InlineMath math="b = 1 - r" /></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Constructing Models from Two Points</h3>
            <ol className="list-decimal pl-5 space-y-2 text-slate-700">
              <li>Given <InlineMath math="(x_1, y_1)" /> and <InlineMath math="(x_2, y_2)" />, substitute both into <InlineMath math="y = ab^x" /></li>
              <li>Divide the equations to solve for <InlineMath math="b" /></li>
              <li>Substitute <InlineMath math="b" /> back to find <InlineMath math="a" /></li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Unit Conversion</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If a population doubles every 5 days, the factor is <InlineMath math="2^{t/5}" /> or <InlineMath math="(2^{1/5})^t" />.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-slate-700"><strong>Compound Interest:</strong> <InlineMath math="A(t) = P(1 + \frac{r}{n})^{nt}" /></p>
              <p className="text-slate-700 mt-2"><strong>Continuous Growth:</strong> <InlineMath math="A(t) = Pe^{rt}" /></p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Competing Function Model Validation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Identifying Patterns in Data</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-slate-700"><strong>Linear:</strong> First differences (output changes) are roughly constant.</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-slate-700"><strong>Quadratic:</strong> Second differences are roughly constant.</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-slate-700"><strong>Exponential:</strong> Ratios of consecutive outputs are roughly constant.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Residuals</h3>
            <KeyTerm 
              term="Residual" 
              definition="Residual = Actual y − Predicted ŷ. Measures the error between observed and predicted values."
            />
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mt-4">
              <h4 className="font-bold text-amber-800">Residual Plot Analysis</h4>
              <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700 mt-2">
                <li><strong>Appropriate Model:</strong> Residual plot looks random (no pattern, unstructured noise)</li>
                <li><strong>Inappropriate Model:</strong> Residual plot shows a clear pattern (e.g., "U" shape)</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: Composition of Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="(f \circ g)(x) = f(g(x))" />
              <p className="text-sm text-slate-600 mt-2">The output of the inner function <InlineMath math="g" /> becomes the input of the outer function <InlineMath math="f" />.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Domain of Composition</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The domain of <InlineMath math="f(g(x))" /> consists of all <InlineMath math="x" /> in the domain of <InlineMath math="g" /> such that <InlineMath math="g(x)" /> is in the domain of <InlineMath math="f" />.
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-slate-700">
              <li>Start with the domain of the inner function <InlineMath math="g" /></li>
              <li>Find values where <InlineMath math="g(x)" /> causes <InlineMath math="f" /> to be undefined</li>
              <li>Exclude those values</li>
            </ol>
          </section>

          <ImportantNote>
            <strong>Non-Commutative:</strong> In general, <InlineMath math="f(g(x)) \neq g(f(x))" />. The order of composition matters!
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-2-8",
      title: "Topic 2.8: Inverse Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concept</h3>
            <KeyTerm 
              term="Inverse Function" 
              definition="A function f⁻¹ that undoes the action of f. It maps the output of f back to the input."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Properties</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-slate-700"><strong>Composition:</strong> <InlineMath math="f(f^{-1}(x)) = x" /> and <InlineMath math="f^{-1}(f(x)) = x" /></p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-slate-700"><strong>Domain/Range Swap:</strong></p>
                <ul className="list-disc pl-5 text-sm text-slate-600 mt-1">
                  <li>Domain of <InlineMath math="f^{-1}" /> = Range of <InlineMath math="f" /></li>
                  <li>Range of <InlineMath math="f^{-1}" /> = Domain of <InlineMath math="f" /></li>
                </ul>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-slate-700"><strong>Graphical:</strong> The graph of <InlineMath math="f^{-1}" /> is the reflection of <InlineMath math="f" /> across the line <InlineMath math="y = x" /></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Existence</h3>
            <ImportantNote>
              A function has an inverse if and only if it is <strong>one-to-one</strong> (passes the Horizontal Line Test). If not, the domain must be restricted.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-9",
      title: "Topic 2.9: Logarithmic Expressions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <KeyTerm 
              term="Logarithm" 
              definition="A logarithm is an exponent. It answers: 'What power must I raise the base to in order to get this number?'"
            />
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-4">
              <BlockMath math="\log_b(a) = c \iff b^c = a" />
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><InlineMath math="b" />: Base (<InlineMath math="b > 0, b \neq 1" />)</li>
                <li><InlineMath math="a" />: Argument (<InlineMath math="a > 0" />)</li>
                <li><InlineMath math="c" />: Exponent (the answer)</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Common vs. Natural Log</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Common Log</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="\log(x)" /> implies base 10</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Natural Log</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="\ln(x)" /> implies base <InlineMath math="e \approx 2.718" /></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Logarithmic Scale</h3>
            <p className="text-slate-700 leading-relaxed">
              On a logarithmic scale, equal distances represent equal <strong>ratios</strong> (multiplicative factors), not equal differences. For example, the distance from 10 to 100 is the same as 100 to 1,000.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-10",
      title: "Topic 2.10: Inverses of Exponential Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Relationship</h3>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mb-4">
              <p className="text-slate-700">
                The logarithmic function is the <strong>inverse</strong> of the exponential function.
              </p>
              <p className="text-slate-700 mt-2">
                If <InlineMath math="f(x) = b^x" />, then <InlineMath math="f^{-1}(x) = \log_b(x)" />
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Covariation Inverse</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Exponential</h4>
                <p className="text-sm text-slate-700 mt-1">As input <strong>adds</strong> (<InlineMath math="\Delta x" />), output <strong>multiplies</strong>.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Logarithmic</h4>
                <p className="text-sm text-slate-700 mt-1">As input <strong>multiplies</strong> (factor of <InlineMath math="b" />), output <strong>adds</strong> (<InlineMath math="\Delta y" />).</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-4">
              <em>Example:</em> For <InlineMath math="y = \log_2(x)" />, doubling the input <InlineMath math="x" /> adds 1 to the output <InlineMath math="y" />.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-11",
      title: "Topic 2.11: Logarithmic Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">General Form</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="f(x) = a \log_b(x) + k" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Characteristics</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><strong>Domain:</strong> <InlineMath math="(0, \infty)" />. The argument must be positive.</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><strong>Range:</strong> <InlineMath math="(-\infty, \infty)" /></p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><strong>Asymptote:</strong> Vertical asymptote at <InlineMath math="x = 0" /> (unless horizontally shifted)</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><strong>Concavity:</strong></p>
                <ul className="list-disc pl-5 text-sm text-slate-600 mt-1">
                  <li>If <InlineMath math="b > 1" /> (growth log): Increasing and <strong>Concave Down</strong></li>
                  <li>If <InlineMath math="0 < b < 1" /> (decay log): Decreasing and <strong>Concave Up</strong></li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">End Behavior</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-slate-700">As <InlineMath math="x \to \infty" />, <InlineMath math="f(x) \to \infty" /> (but very slowly)</p>
              <p className="text-slate-700 mt-2">As <InlineMath math="x \to 0^+" />, <InlineMath math="f(x) \to -\infty" /></p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-12",
      title: "Topic 2.12: Logarithmic Function Manipulation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Log Laws</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Product Rule</h4>
                <BlockMath math="\log_b(mn) = \log_b(m) + \log_b(n)" />
                <p className="text-sm text-slate-600 mt-2"><em>Transformation:</em> <InlineMath math="\log(bx) = \log b + \log x" />. A horizontal compression inside a log is equivalent to a <strong>vertical shift</strong>.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Quotient Rule</h4>
                <BlockMath math="\log_b(m/n) = \log_b(m) - \log_b(n)" />
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Power Rule</h4>
                <BlockMath math="\log_b(m^p) = p \cdot \log_b(m)" />
                <p className="text-sm text-slate-600 mt-2"><em>Transformation:</em> <InlineMath math="\log(x^2) = 2\log x" />. A power inside a log is equivalent to a <strong>vertical stretch</strong>.</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-800">Change of Base Formula</h4>
                <BlockMath math="\log_b(a) = \frac{\ln a}{\ln b}" />
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-13",
      title: "Topic 2.13: Exponential and Logarithmic Equations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Solving Exponential Equations</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Method 1: Common Base</h4>
                <p className="text-sm text-slate-700 mt-1">If <InlineMath math="b^x = b^y" />, then <InlineMath math="x = y" /></p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Method 2: Using Logs</h4>
                <p className="text-sm text-slate-700 mt-1">Isolate the exponential term, then take the log of both sides.</p>
                <p className="text-sm text-slate-600 mt-2"><em>Example:</em> <InlineMath math="3 \cdot e^{2x} = 15 \to e^{2x} = 5 \to 2x = \ln(5) \to x = \frac{\ln(5)}{2}" /></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Solving Logarithmic Equations</h3>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Method 1: Exponentiating</h4>
                <p className="text-sm text-slate-700 mt-1">Rewrite <InlineMath math="\log_b(x) = y" /> as <InlineMath math="b^y = x" /></p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-800">Method 2: One-to-One Property</h4>
                <p className="text-sm text-slate-700 mt-1">If <InlineMath math="\log_b(x) = \log_b(y)" />, then <InlineMath math="x = y" /></p>
              </div>
            </div>
          </section>

          <ImportantNote>
            <strong>Extraneous Solutions:</strong> You <strong>must</strong> check answers to ensure they do not result in taking the logarithm of a negative number or zero.
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-2-14",
      title: "Topic 2.14: Logarithmic Function Context and Data Modeling",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">When to Use Logarithmic Models</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Logarithmic functions model phenomena where rapid changes slow down over time, or where the perception of intensity is relative to ratios.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-800">Real-World Examples</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li>Richter scale (earthquakes)</li>
                <li>Decibels (sound intensity)</li>
                <li>pH scale (acidity)</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Identifying Log Data</h3>
            <Mnemonic 
              title="Pattern Recognition"
              content="If inputs (x) increase multiplicatively (geometric sequence) and outputs (y) increase additively (arithmetic sequence), a logarithmic model is appropriate."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-2-15",
      title: "Topic 2.15: Semi-log Plots",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Purpose</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Semi-log plots are used to "linearize" curved data to verify if it follows an exponential model.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Transformation</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-slate-700 mb-2">
                <strong>Semi-log y-axis:</strong> Plot <InlineMath math="(x, \log y)" /> instead of <InlineMath math="(x, y)" />.
              </p>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-slate-700 mb-2"><strong>Why it works:</strong> If <InlineMath math="y = ab^x" />, taking the log of both sides:</p>
              <BlockMath math="\log y = \log(ab^x)" />
              <BlockMath math="\log y = \log a + \log(b^x)" />
              <BlockMath math="\log y = (\log b)x + \log a" />
              <p className="text-sm text-slate-600 mt-2">This is in the form of a line <InlineMath math="Y = MX + B" />, where <InlineMath math="Y = \log y" />, slope <InlineMath math="M = \log b" />, and intercept <InlineMath math="B = \log a" />.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting the Plot</h3>
            <ImportantNote>
              If graphing <InlineMath math="(x, \log y)" /> produces a <strong>straight line</strong>, the original data represents an <strong>Exponential Function</strong>.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "summary-2",
      title: "Unit 2 Summary: Key Skills",
      content: (
        <div className="space-y-6">
          <section>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Sequence Recognition</h4>
                  <p className="text-sm text-slate-700 mt-1">Distinguish arithmetic (constant difference) from geometric (constant ratio) sequences.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Exponential Modeling</h4>
                  <p className="text-sm text-slate-700 mt-1">Build models using <InlineMath math="f(x) = ab^x" /> with growth factor <InlineMath math="b = 1 \pm r" />.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="bg-purple-200 text-purple-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Log Laws</h4>
                  <p className="text-sm text-slate-700 mt-1">Apply product, quotient, power rules and change of base formula.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="bg-amber-200 text-amber-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Solving Equations</h4>
                  <p className="text-sm text-slate-700 mt-1">Use logs to solve exponentials; exponentiate to solve log equations. Check for extraneous solutions!</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="bg-red-200 text-red-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Semi-log Plots</h4>
                  <p className="text-sm text-slate-700 mt-1">Plot <InlineMath math="(x, \log y)" />. If linear, data is exponential.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default precalculusUnit2Notes;
